import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Card,
  Space,
  Button,
  Table,
  Form,
  Input,
  Modal,
  Popconfirm,
  Tag,
  Badge,
  message,
  Row,
  Col,
  Select,
  Typography,
  Tooltip,
  Drawer,
  Divider,
  Alert,
  Segmented,
  Timeline,
  Dropdown,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  BranchesOutlined,
  PrinterOutlined,
  BarcodeOutlined,
  EyeOutlined,
  HistoryOutlined,
  SettingOutlined,
  CopyOutlined,
  DownloadOutlined,
  CodeOutlined,
  RotateRightOutlined,
  FundProjectionScreenOutlined,
  ClearOutlined,
  CheckOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { admin } from '../../services/api';
import ZplCanvasRenderer from '../../components/ZplCanvasRenderer';
import './LabelTemplatesManagement.css';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Variáveis dinâmicas comuns utilizadas em etiquetas logísticas SSW
const SSW_TAGS = [
  { label: '{chave} - Chave da NFe (44 dígitos)', value: '{chave}' },
  { label: '{nota} - Número da Nota Fiscal', value: '{nota}' },
  { label: '{serie} - Série da Nota', value: '{serie}' },
  { label: '{remetente} - Nome do Remetente', value: '{remetente}' },
  { label: '{cdremetente} - CNPJ/Código Remetente', value: '{cdremetente}' },
  { label: '{destinatario} - Nome do Destinatário', value: '{destinatario}' },
  { label: '{cddestinatario} - CNPJ/CPF Destinatário', value: '{cddestinatario}' },
  { label: '{cidade} - Cidade de Destino', value: '{cidade}' },
  { label: '{sigla} - Sigla da Praça / Filial', value: '{sigla}' },
  { label: '{rota} - Código da Rota', value: '{rota}' },
  { label: '{volumes} - Quantidade de Volumes', value: '{volumes}' },
  { label: '{peso} - Peso Total (Kg)', value: '{peso}' },
  { label: '{m3} - Metragem Cúbica', value: '{m3}' },
  { label: '{nr1} - Etiqueta Início / Número 1', value: '{nr1}' },
  { label: '{nr2} - Etiqueta Fim / Número 2', value: '{nr2}' },
  { label: '{pedido} - Número do Pedido', value: '{pedido}' },
  { label: '{nrtransporte} - Número de Transporte SSW', value: '{nrtransporte}' },
];

// ZPL Padrão de Exemplo para inicialização
const SAMPLE_ZPL = `^XA
^PW800
^LL1200
^LH0,0

^FO40,40^GB720,1120,4^FS

^FO60,70^A0N,45,45^FDTRANSPORTE LOGISTICO^FS
^FO60,125^A0N,28,28^FDRota: {rota} - Praca: {sigla}^FS
^FO550,70^A0N,55,55^FD{sigla}^FS

^FO40,180^GB720,2,2^FS

^FO60,205^A0N,24,24^FDREMETENTE:^FS
^FO60,235^A0N,30,30^FD{remetente}^FS
^FO60,275^A0N,22,22^FDCNPJ: {cdremetente}^FS

^FO40,315^GB720,2,2^FS

^FO60,335^A0N,24,24^FDDESTINATARIO:^FS
^FO60,365^A0N,32,32^FD{destinatario}^FS
^FO60,405^A0N,24,24^FDCidade: {cidade}^FS

^FO40,450^GB720,2,2^FS

^FO60,475^A0N,26,26^FDNF: {nota} - SERIE: {serie}^FS
^FO420,475^A0N,26,26^FDVOLUMES: {volumes}^FS
^FO60,515^A0N,26,26^FDPESO: {peso} KG^FS
^FO420,515^A0N,26,26^FDM3: {m3}^FS

^FO40,560^GB720,2,2^FS

^FO100,600^BY3,3,140
^BCN,140,Y,N,N
^FD>;>{chave}^FS

^FO40,820^GB720,2,2^FS

^FO60,850^A0N,28,28^FDSIGLA OPERACIONAL:^FS
^FO60,890^A0N,70,70^FD{rota} - {sigla}^FS
^FO500,860^A0N,32,32^FDETIQ: {nr1}/{nr2}^FS

^XZ`;

export default function LabelTemplatesManagement() {
  // Lista de templates
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterEmulator, setFilterEmulator] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL');

  // Emuladores
  const [emulators, setEmulators] = useState([]);
  const [emulatorsLoading, setEmulatorsLoading] = useState(false);
  const [emulatorsModalOpen, setEmulatorsModalOpen] = useState(false);
  const [editingEmulator, setEditingEmulator] = useState(null);
  const [emulatorForm] = Form.useForm();

  // Histórico de Auditoria
  const [audits, setAudits] = useState([]);
  const [auditsLoading, setAuditsLoading] = useState(false);
  const [auditsDrawerOpen, setAuditsDrawerOpen] = useState(false);
  const [selectedAuditForDiff, setSelectedAuditForDiff] = useState(null);
  const [diffModalOpen, setDiffModalOpen] = useState(false);

  // Modo Editor Split-View (Lado Esquerdo Código / Lado Direito Preview)
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null); // null = criar novo
  const [isNewVersionMode, setIsNewVersionMode] = useState(false);
  const [editorForm] = Form.useForm();

  // Código ZPL atual sendo digitado no editor
  const [activeCode, setActiveCode] = useState('');
  const [activeEmulatorId, setActiveEmulatorId] = useState('');

  // Controles de Renderização da Etiqueta (Lado Direito)
  const [renderMode, setRenderMode] = useState('labelary'); // 'labelary' ou 'canvas'
  const [labelDpmm, setLabelDpmm] = useState('8dpmm'); // 6dpmm, 8dpmm, 12dpmm
  const [labelSize, setLabelSize] = useState('4x6'); // 4x6, 4x3, 4x4
  const [labelRotation, setLabelRotation] = useState(0); // 0, 90, 180, 270
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);

  const textAreaRef = useRef(null);

  // Carregar templates
  const loadTemplates = async () => {
    setLoading(true);
    try {
      const res = await admin.getLabelTemplates({});
      setItems(res.data || []);
    } catch (e) {
      message.error(e?.message || 'Erro ao listar modelos de etiqueta');
    } finally {
      setLoading(false);
    }
  };

  // Carregar emuladores
  const loadEmulators = async () => {
    setEmulatorsLoading(true);
    try {
      const data = await admin.getLabelTemplateEmulators();
      setEmulators(data || []);
    } catch (e) {
      console.warn('Erro ao carregar emuladores:', e);
    } finally {
      setEmulatorsLoading(false);
    }
  };

  // Carregar auditoria
  const loadAudits = async (templateId = null) => {
    setAuditsLoading(true);
    try {
      const data = await admin.getLabelTemplateAudits(templateId ? { templateId } : {});
      setAudits(data || []);
      setAuditsDrawerOpen(true);
    } catch (e) {
      message.error(e?.message || 'Erro ao carregar registros de auditoria');
    } finally {
      setAuditsLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
    loadEmulators();
  }, []);

  // Dados filtrados de templates
  const filteredTemplates = useMemo(() => {
    return items.filter((item) => {
      const matchSearch =
        !search ||
        (item.name && item.name.toLowerCase().includes(search.toLowerCase())) ||
        (item.version && item.version.toLowerCase().includes(search.toLowerCase()));

      const matchEmulator =
        filterEmulator === 'ALL' ||
        (filterEmulator === 'NONE' && !item.emulatorId) ||
        item.emulatorId === filterEmulator;

      const matchStatus =
        filterStatus === 'ALL' ||
        (filterStatus === 'ACTIVE' && item.isActive) ||
        (filterStatus === 'INACTIVE' && !item.isActive);

      return matchSearch && matchEmulator && matchStatus;
    });
  }, [items, search, filterEmulator, filterStatus]);

  // Abertura do Editor (Novo ou Edição)
  const handleOpenEditor = (template = null, asNewVersion = false) => {
    setEditingTemplate(template);
    setIsNewVersionMode(asNewVersion);

    if (template) {
      const codeValue = template.code || '';
      const versionValue = asNewVersion ? '' : template.version;
      const emulValue = template.emulatorId || (emulators[0]?.id || '');

      editorForm.setFieldsValue({
        name: template.name,
        version: versionValue,
        emulatorId: emulValue,
        code: codeValue,
      });
      setActiveCode(codeValue);
      setActiveEmulatorId(emulValue);
    } else {
      const defaultEmul = emulators[0]?.id || '';
      editorForm.setFieldsValue({
        name: '',
        version: '1.0.0',
        emulatorId: defaultEmul,
        code: SAMPLE_ZPL,
      });
      setActiveCode(SAMPLE_ZPL);
      setActiveEmulatorId(defaultEmul);
    }

    setEditorOpen(true);
  };

  // Salvar Template no Editor
  const handleSaveEditor = async () => {
    try {
      const values = await editorForm.validateFields();
      setLoading(true);

      if (isNewVersionMode && editingTemplate) {
        // Criar Nova Versão
        await admin.createLabelTemplateVersion(editingTemplate.id, {
          version: values.version,
          code: activeCode,
          emulatorId: values.emulatorId,
        });
        message.success(`Nova versão "${values.version}" criada e ativada com sucesso!`);
      } else if (editingTemplate) {
        // Atualizar Existente
        await admin.updateLabelTemplate(editingTemplate.id, {
          name: values.name,
          version: values.version,
          code: activeCode,
          emulatorId: values.emulatorId,
        });
        message.success('Modelo de etiqueta atualizado com sucesso (auditado)!');
      } else {
        // Criar Novo
        await admin.createLabelTemplate({
          name: values.name,
          version: values.version,
          code: activeCode,
          emulatorId: values.emulatorId,
        });
        message.success('Novo modelo de etiqueta criado com sucesso!');
      }

      setEditorOpen(false);
      setEditingTemplate(null);
      setIsNewVersionMode(false);
      await loadTemplates();
    } catch (e) {
      if (e?.message) message.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Inserir Tag Dinâmica na Posição do Cursor
  const handleInsertTag = (tag) => {
    const textarea = textAreaRef.current?.resizableTextArea?.textArea;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const current = activeCode;
      const newText = current.substring(0, start) + tag + current.substring(end);
      setActiveCode(newText);
      editorForm.setFieldsValue({ code: newText });

      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + tag.length, start + tag.length);
      }, 50);
    } else {
      const newText = (activeCode || '') + tag;
      setActiveCode(newText);
      editorForm.setFieldsValue({ code: newText });
    }
    message.info(`Tag "${tag}" inserida no código`);
  };

  // Carregar ZPL Padrão
  const handleLoadSample = () => {
    Modal.confirm({
      title: 'Carregar Exemplo ZPL Padrão?',
      content: 'Isto substituirá o código atual pelo modelo de exemplo logístico SSW.',
      okText: 'Substituir',
      cancelText: 'Cancelar',
      onOk: () => {
        setActiveCode(SAMPLE_ZPL);
        editorForm.setFieldsValue({ code: SAMPLE_ZPL });
        message.success('Exemplo ZPL carregado');
      },
    });
  };

  // Copiar ZPL
  const handleCopyCode = () => {
    if (!activeCode) return;
    navigator.clipboard.writeText(activeCode);
    message.success('Código copiado para a área de transferência');
  };

  // Ativar Versão
  const handleActivate = async (item) => {
    try {
      await admin.activateLabelTemplate(item.id);
      message.success(`Versão "${item.version}" de "${item.name}" ativada!`);
      await loadTemplates();
    } catch (e) {
      message.error(e?.message || 'Erro ao ativar versão');
    }
  };

  // Excluir Template
  const handleDelete = async (item) => {
    try {
      await admin.deleteLabelTemplate(item.id);
      message.success(`Modelo "${item.name}" excluído (ação auditada)`);
      await loadTemplates();
    } catch (e) {
      message.error(e?.message || 'Erro ao excluir modelo');
    }
  };

  // Emuladores - Salvar
  const handleSaveEmulator = async () => {
    try {
      const values = await emulatorForm.validateFields();
      if (editingEmulator) {
        await admin.updateLabelTemplateEmulator(editingEmulator.id, values);
        message.success('Emulador atualizado com sucesso (auditado)');
      } else {
        await admin.createLabelTemplateEmulator(values);
        message.success('Emulador cadastrado com sucesso (auditado)');
      }
      setEditingEmulator(null);
      emulatorForm.resetFields();
      await loadEmulators();
    } catch (e) {
      if (e?.message) message.error(e.message);
    }
  };

  // Emuladores - Excluir
  const handleDeleteEmulator = async (emul) => {
    try {
      await admin.deleteLabelTemplateEmulator(emul.id);
      message.success(`Emulador "${emul.name}" removido (auditado)`);
      await loadEmulators();
    } catch (e) {
      message.error(e?.message || 'Erro ao excluir emulador');
    }
  };

  // Código com variáveis simuladas para o preview
  const previewZplCode = useMemo(() => {
    if (!activeCode) return '';
    return activeCode
      .replace(/{chave}/g, '35240612345678000199550010000012341234567890')
      .replace(/{nota}/g, '1234')
      .replace(/{serie}/g, '1')
      .replace(/{remetente}/g, 'DISTRIBUIDORA EXEMPLO LTDA')
      .replace(/{cdremetente}/g, '12.345.678/0001-99')
      .replace(/{destinatario}/g, 'LOGISTICA & TRANSPORTES EXPRESS')
      .replace(/{cddestinatario}/g, '98.765.432/0001-11')
      .replace(/{cidade}/g, 'SAO PAULO - SP')
      .replace(/{sigla}/g, 'SPO')
      .replace(/{rota}/g, 'R01')
      .replace(/{volumes}/g, '10')
      .replace(/{peso}/g, '45.50')
      .replace(/{m3}/g, '0.350')
      .replace(/{nr1}/g, '1')
      .replace(/{nr2}/g, '10')
      .replace(/{pedido}/g, 'PED-89421')
      .replace(/{nrtransporte}/g, 'TR-99432');
  }, [activeCode]);

  // URL da Labelary API para renderizar o ZPL em alta definição
  const labelaryUrl = useMemo(() => {
    if (!previewZplCode) return '';
    try {
      const cleanZpl = previewZplCode.trim();
      const encoded = encodeURIComponent(cleanZpl);
      // Exemplo: http://api.labelary.com/v1/printers/8dpmm/labels/4x6/0/
      return `https://api.labelary.com/v1/printers/${labelDpmm}/labels/${labelSize}/0/${encoded}`;
    } catch {
      return '';
    }
  }, [previewZplCode, labelDpmm, labelSize]);

  // Colunas da Tabela de Templates
  const columns = [
    {
      title: 'Nome do Modelo',
      dataIndex: 'name',
      key: 'name',
      width: 260,
      render: (name, record) => (
        <Space direction="vertical" size={2}>
          <Text strong style={{ fontSize: 14 }}>{name}</Text>
          <Text type="secondary" code style={{ fontSize: 11 }}>
            ID: {record.id ? `${record.id.slice(0, 10)}...` : '-'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Versão',
      dataIndex: 'version',
      key: 'version',
      width: 110,
      render: (version) => (
        <Tag color="blue" icon={<BranchesOutlined />} style={{ fontWeight: 600 }}>
          {version}
        </Tag>
      ),
    },
    {
      title: 'Emulador / Linguagem',
      key: 'emulator',
      width: 170,
      render: (_, record) => {
        const emul = record.label_template_emulators;
        return emul ? (
          <Tag color="geekblue" icon={<BarcodeOutlined />} className="ltm-emulator-tag">
            {emul.name}
          </Tag>
        ) : (
          <Tag color="default">Não definido</Tag>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 120,
      render: (active) => (
        <Badge
          status={active ? 'success' : 'default'}
          text={
            <span style={{ fontWeight: active ? 600 : 400, color: active ? '#52c41a' : undefined }}>
              {active ? 'Ativo' : 'Inativo'}
            </span>
          }
        />
      ),
    },
    {
      title: 'Última Modificação',
      key: 'updated',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Text style={{ fontSize: 12 }}>
            {record.updatedAt ? new Date(record.updatedAt).toLocaleString('pt-BR') : '-'}
          </Text>
          {record.updatedBy?.name && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              Por: {record.updatedBy.name}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 220,
      fixed: 'right',
      render: (_, item) => (
        <Space size={6} wrap>
          {/* Abrir no Editor Split View */}
          <Tooltip title="Abrir no Editor Lado a Lado">
            <Button
              type="primary"
              size="small"
              icon={<CodeOutlined />}
              onClick={() => handleOpenEditor(item)}
            >
              Editor
            </Button>
          </Tooltip>

          {/* Ativar Versão */}
          {!item.isActive && (
            <Tooltip title="Ativar esta versão como a padrão de impressão">
              <Button
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => handleActivate(item)}
              >
                Ativar
              </Button>
            </Tooltip>
          )}

          {/* Criar Nova Versão */}
          <Tooltip title="Criar Nova Versão baseada neste modelo">
            <Button
              size="small"
              icon={<BranchesOutlined />}
              onClick={() => handleOpenEditor(item, true)}
            />
          </Tooltip>

          {/* Auditoria do Modelo */}
          <Tooltip title="Ver Histórico de Alterações / Auditoria">
            <Button
              size="small"
              icon={<HistoryOutlined />}
              onClick={() => loadAudits(item.id)}
            />
          </Tooltip>

          {/* Excluir Modelo */}
          <Popconfirm
            title="Excluir modelo de etiqueta?"
            description={`Tem certeza que deseja remover "${item.name}" v${item.version}? Esta ação será registrada na auditoria.`}
            onConfirm={() => handleDelete(item)}
            okText="Sim, excluir"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Excluir Modelo">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="ltm-container">
      {/* Cabeçalho da Página */}
      <Card className="ltm-header-card">
        <div className="ltm-header-flex">
          <div className="ltm-header-title-box">
            <div className="ltm-printer-avatar" style={{ width: 52, height: 52 }}>
              <PrinterOutlined style={{ fontSize: 26 }} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Title level={2} style={{ margin: 0 }}>Modelos de Etiqueta (Templates)</Title>
                <Tag color="blue" style={{ fontSize: 12, padding: '2px 8px', borderRadius: 10 }}>
                  ZPL / EPL
                </Tag>
              </div>
              <Text type="secondary">
                Editor de layout com visualização em tempo real, versionamento, emuladores e trilha de auditoria completa.
              </Text>
            </div>
          </div>

          <Space wrap align="center">
            <Button
              icon={<HistoryOutlined />}
              onClick={() => loadAudits(null)}
              loading={auditsLoading}
            >
              Trilha de Auditoria
            </Button>
            <Button
              icon={<SettingOutlined />}
              onClick={() => setEmulatorsModalOpen(true)}
            >
              Gerenciar Emuladores ({emulators.length})
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadTemplates}
              loading={loading}
            >
              Atualizar
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              style={{ backgroundColor: '#1677ff' }}
              onClick={() => handleOpenEditor(null)}
            >
              Novo Modelo
            </Button>
          </Space>
        </div>
      </Card>

      {/* Conteúdo Principal: Tabela de Modelos */}
      {!editorOpen && (
        <Card className="ltm-main-card">
          {/* Filtros e Busca */}
          <Row gutter={[16, 16]} align="middle" justify="space-between" style={{ marginBottom: 20 }}>
            <Col xs={24} sm={12} md={8}>
              <Input
                placeholder="Buscar modelo por nome ou versão..."
                allowClear
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="middle"
              />
            </Col>

            <Col xs={24} sm={12} md={16}>
              <Space wrap style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Select
                  value={filterEmulator}
                  onChange={setFilterEmulator}
                  style={{ width: 170 }}
                >
                  <Option value="ALL">Todos os emuladores</Option>
                  {emulators.map((emul) => (
                    <Option key={emul.id} value={emul.id}>
                      {emul.name}
                    </Option>
                  ))}
                  <Option value="NONE">Sem emulador</Option>
                </Select>

                <Select
                  value={filterStatus}
                  onChange={setFilterStatus}
                  style={{ width: 140 }}
                >
                  <Option value="ALL">Todos os status</Option>
                  <Option value="ACTIVE">Apenas Ativos</Option>
                  <Option value="INACTIVE">Apenas Inativos</Option>
                </Select>

                <Text type="secondary" style={{ fontSize: 13 }}>
                  Exibindo <strong>{filteredTemplates.length}</strong> de <strong>{items.length}</strong> modelos
                </Text>
              </Space>
            </Col>
          </Row>

          <Table
            dataSource={filteredTemplates}
            columns={columns}
            rowKey="id"
            loading={loading}
            scroll={{ x: 1080 }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              showTotal: (total) => `Total: ${total} modelos`,
            }}
          />
        </Card>
      )}

      {/* SEÇÃO ESTILO EDITOR LADO A LADO (Split View) */}
      {editorOpen && (
        <Card
          className="ltm-main-card"
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <Space>
                <Button icon={<ArrowLeftOutlined />} onClick={() => setEditorOpen(false)}>
                  Voltar à Lista
                </Button>
                <Title level={4} style={{ margin: 0 }}>
                  {isNewVersionMode
                    ? `Criar Nova Versão de "${editingTemplate?.name}"`
                    : editingTemplate
                    ? `Editar Modelo: "${editingTemplate?.name}" (v${editingTemplate?.version})`
                    : 'Novo Modelo de Etiqueta'}
                </Title>
              </Space>

              <Space>
                <Button onClick={() => setEditorOpen(false)}>Cancelar</Button>
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={handleSaveEditor}
                  loading={loading}
                  style={{ backgroundColor: '#1677ff' }}
                >
                  {isNewVersionMode ? 'Criar e Ativar Versão' : 'Salvar Alterações'}
                </Button>
              </Space>
            </div>
          }
        >
          {/* Informações Básicas do Modelo */}
          <Form form={editorForm} layout="vertical" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="name"
                  label="Nome do Modelo"
                  rules={[{ required: true, message: 'Informe o nome do modelo' }]}
                >
                  <Input placeholder="Ex: Etiqueta Padrão 100x150" disabled={isNewVersionMode} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  name="version"
                  label={isNewVersionMode ? 'Número da Nova Versão' : 'Versão'}
                  rules={[{ required: true, message: 'Informe a versão' }]}
                  extra={isNewVersionMode ? `Versão base: ${editingTemplate?.version}` : undefined}
                >
                  <Input placeholder="Ex: 1.0.1 ou v2" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  name="emulatorId"
                  label="Emulador / Linguagem"
                  rules={[{ required: true, message: 'Selecione o emulador' }]}
                >
                  <Select
                    placeholder="Selecione o emulador (ex: ZPL)"
                    onChange={(val) => setActiveEmulatorId(val)}
                  >
                    {emulators.map((emul) => (
                      <Option key={emul.id} value={emul.id}>
                        {emul.name} {emul.description ? `(${emul.description})` : ''}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={4} style={{ display: 'flex', alignItems: 'center', paddingTop: 8 }}>
                <Alert
                  type="info"
                  showIcon
                  message="Auditado"
                  description="Qualquer alteração será registrada com seu usuário e data."
                  style={{ padding: '6px 10px', width: '100%' }}
                />
              </Col>
            </Row>
          </Form>

          {/* SPLIT VIEW: LADO ESQUERDO CÓDIGO | LADO DIREITO RENDERIZAÇÃO */}
          <div className="ltm-editor-layout">
            {/* LADO ESQUERDO: EDITOR DE CÓDIGO */}
            <div className="ltm-code-panel">
              {/* Barra de Ferramentas do Editor */}
              <div className="ltm-editor-toolbar">
                <Space wrap size={6}>
                  <Text strong style={{ fontSize: 13 }}>
                    <CodeOutlined /> Código do Template
                  </Text>

                  {/* Menu de Inserção de Tags/Variáveis */}
                  <Dropdown
                    menu={{
                      items: SSW_TAGS.map((t, idx) => ({
                        key: idx.toString(),
                        label: t.label,
                        onClick: () => handleInsertTag(t.value),
                      })),
                    }}
                    trigger={['click']}
                  >
                    <Button size="small" type="dashed">
                      + Inserir Variável SSW
                    </Button>
                  </Dropdown>
                </Space>

                <Space size={4}>
                  <Tooltip title="Carregar modelo de exemplo padrão">
                    <Button size="small" icon={<FundProjectionScreenOutlined />} onClick={handleLoadSample}>
                      Exemplo
                    </Button>
                  </Tooltip>
                  <Tooltip title="Copiar código para área de transferência">
                    <Button size="small" icon={<CopyOutlined />} onClick={handleCopyCode} />
                  </Tooltip>
                  <Tooltip title="Limpar código">
                    <Button
                      size="small"
                      icon={<ClearOutlined />}
                      onClick={() => {
                        setActiveCode('');
                        editorForm.setFieldsValue({ code: '' });
                      }}
                    />
                  </Tooltip>
                </Space>
              </div>

              {/* TextArea Editor com Estilo Código */}
              <TextArea
                ref={textAreaRef}
                value={activeCode}
                onChange={(e) => setActiveCode(e.target.value)}
                placeholder="Insira o código de impressão (ex: comandos ZPL ^XA ... ^XZ)"
                className="ltm-textarea-editor"
                autoSize={{ minRows: 22, maxRows: 34 }}
                spellCheck={false}
              />

              {/* Rodapé do Editor */}
              <div className="ltm-editor-footer">
                <span>
                  Linhas: <strong>{activeCode ? activeCode.split('\n').length : 0}</strong> | Caracteres: <strong>{activeCode.length}</strong>
                </span>
                <span>
                  Linguagem:{' '}
                  <strong>
                    {emulators.find((e) => e.id === activeEmulatorId)?.name || 'ZPL'}
                  </strong>
                </span>
              </div>
            </div>

            {/* LADO DIREITO: RENDERIZAÇÃO DA ETIQUETA */}
            <div className="ltm-preview-panel">
              {/* Barra de Ferramentas da Prévia */}
              <div className="ltm-preview-toolbar">
                <Space wrap size={8}>
                  <Text strong style={{ fontSize: 13 }}>
                    <EyeOutlined /> Renderização em Tempo Real
                  </Text>

                  <Segmented
                    value={renderMode}
                    onChange={setRenderMode}
                    options={[
                      { label: 'Labelary (Real)', value: 'labelary' },
                      { label: 'Canvas Local', value: 'canvas' },
                    ]}
                    size="small"
                  />
                </Space>

                <Space wrap size={6}>
                  <Select
                    value={labelDpmm}
                    onChange={setLabelDpmm}
                    size="small"
                    style={{ width: 110 }}
                    title="Densidade de Impressão"
                  >
                    <Option value="8dpmm">8 dpmm (203 dpi)</Option>
                    <Option value="12dpmm">12 dpmm (300 dpi)</Option>
                    <Option value="6dpmm">6 dpmm (152 dpi)</Option>
                  </Select>

                  <Select
                    value={labelSize}
                    onChange={setLabelSize}
                    size="small"
                    style={{ width: 90 }}
                    title="Dimensão da Etiqueta"
                  >
                    <Option value="4x6">4 x 6"</Option>
                    <Option value="4x4">4 x 4"</Option>
                    <Option value="4x3">4 x 3"</Option>
                    <Option value="4x8">4 x 8"</Option>
                  </Select>

                  <Tooltip title="Rotacionar Prévia (90°)">
                    <Button
                      size="small"
                      icon={<RotateRightOutlined />}
                      onClick={() => setLabelRotation((prev) => (prev + 90) % 360)}
                    />
                  </Tooltip>

                  <Tooltip title="Atualizar Prévia">
                    <Button
                      size="small"
                      icon={<ReloadOutlined />}
                      onClick={() => setPreviewRefreshKey((k) => k + 1)}
                    />
                  </Tooltip>
                </Space>
              </div>

              {/* Mesa de Trabalho (Label Workbench Board) */}
              <div className="ltm-label-board">
                {activeCode.trim().length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#8c8c8c' }}>
                    <BarcodeOutlined style={{ fontSize: 48, marginBottom: 12, opacity: 0.5 }} />
                    <Paragraph type="secondary">
                      Digite ou cole o código ZPL no lado esquerdo para visualizar a etiqueta aqui.
                    </Paragraph>
                  </div>
                ) : renderMode === 'labelary' ? (
                  <div
                    className="ltm-label-sheet"
                    style={{
                      transform: `rotate(${labelRotation}deg)`,
                      transformOrigin: 'center center',
                    }}
                  >
                    <Spin spinning={previewLoading}>
                      <img
                        key={previewRefreshKey}
                        src={labelaryUrl}
                        alt="Prévia ZPL Labelary"
                        onLoad={() => setPreviewLoading(false)}
                        onLoadStart={() => setPreviewLoading(true)}
                        onError={() => {
                          setPreviewLoading(false);
                        }}
                        style={{
                          minHeight: 280,
                          backgroundColor: '#ffffff',
                        }}
                      />
                    </Spin>
                  </div>
                ) : (
                  <div
                    className="ltm-label-sheet ltm-label-canvas-wrap"
                    style={{
                      transform: `rotate(${labelRotation}deg)`,
                      transformOrigin: 'center center',
                    }}
                  >
                    <ZplCanvasRenderer zpl={previewZplCode} width={380} height={550} />
                  </div>
                )}
              </div>

              {/* Rodapé Informativo da Prévia */}
              <div className="ltm-editor-footer">
                <span>
                  Prévia simulando dados reais da NFe (Chave, Remetente, Destinatário, Rota).
                </span>
                {labelaryUrl && renderMode === 'labelary' && (
                  <a href={labelaryUrl} target="_blank" rel="noopener noreferrer" download="etiqueta-preview.png">
                    <DownloadOutlined /> Baixar PNG
                  </a>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* MODAL DE GESTÃO DE EMULADORES */}
      <Modal
        open={emulatorsModalOpen}
        title={
          <Space>
            <SettingOutlined style={{ color: '#1677ff' }} />
            <span>Gerenciamento de Emuladores de Impressão (`label_template_emulators`)</span>
          </Space>
        }
        onCancel={() => {
          setEmulatorsModalOpen(false);
          setEditingEmulator(null);
          emulatorForm.resetFields();
        }}
        footer={null}
        width={720}
        destroyOnClose
      >
        <div style={{ marginTop: 12 }}>
          <Paragraph type="secondary">
            Cadastre e configure linguagens de emulação suportadas para impressão de etiquetas (ex: ZPL, EPL, TSPL).
            Todas as alterações são registradas na trilha de auditoria.
          </Paragraph>

          {/* Formulário de Criação / Edição de Emulador */}
          <Card size="small" style={{ marginBottom: 16, background: 'rgba(0,0,0,0.02)' }}>
            <Form
              form={emulatorForm}
              layout="vertical"
              onFinish={handleSaveEmulator}
              initialValues={{ name: '', description: '' }}
            >
              <Row gutter={12}>
                <Col xs={24} sm={8}>
                  <Form.Item
                    name="name"
                    label="Nome da Linguagem / Emulador"
                    rules={[{ required: true, message: 'Informe o nome (ex: ZPL)' }]}
                  >
                    <Input placeholder="Ex: ZPL, EPL" style={{ textTransform: 'uppercase' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="description" label="Descrição">
                    <Input placeholder="Ex: Zebra Programming Language (Térmica)" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={4} style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 24 }}>
                  <Button type="primary" htmlType="submit" style={{ width: '100%' }}>
                    {editingEmulator ? 'Atualizar' : 'Adicionar'}
                  </Button>
                </Col>
              </Row>
              {editingEmulator && (
                <Button
                  size="small"
                  onClick={() => {
                    setEditingEmulator(null);
                    emulatorForm.resetFields();
                  }}
                >
                  Cancelar Edição
                </Button>
              )}
            </Form>
          </Card>

          {/* Tabela de Emuladores */}
          <Table
            dataSource={emulators}
            rowKey="id"
            size="small"
            loading={emulatorsLoading}
            pagination={false}
            columns={[
              {
                title: 'Linguagem / Nome',
                dataIndex: 'name',
                key: 'name',
                render: (name) => (
                  <Tag color="blue" style={{ fontWeight: 600 }}>
                    {name}
                  </Tag>
                ),
              },
              {
                title: 'Descrição',
                dataIndex: 'description',
                key: 'description',
                render: (desc) => desc || <Text type="secondary">-</Text>,
              },
              {
                title: 'Status',
                dataIndex: 'isActive',
                key: 'isActive',
                width: 100,
                render: (active) => (
                  <Tag color={active ? 'green' : 'default'}>{active ? 'Ativo' : 'Inativo'}</Tag>
                ),
              },
              {
                title: 'Ações',
                key: 'actions',
                width: 120,
                render: (_, item) => (
                  <Space size={4}>
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => {
                        setEditingEmulator(item);
                        emulatorForm.setFieldsValue({
                          name: item.name,
                          description: item.description,
                        });
                      }}
                    />
                    <Popconfirm
                      title="Excluir emulador?"
                      description="Esta ação será registrada na auditoria e não poderá ser desfeita se houver modelos vinculados."
                      onConfirm={() => handleDeleteEmulator(item)}
                      okText="Sim"
                      cancelText="Não"
                    >
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                ),
              },
            ]}
          />
        </div>
      </Modal>

      {/* DRAWER DE HISTÓRICO DE AUDITORIA */}
      <Drawer
        open={auditsDrawerOpen}
        title={
          <Space>
            <HistoryOutlined style={{ color: '#1677ff' }} />
            <span>Trilha de Auditoria de Modelos de Etiqueta</span>
          </Space>
        }
        onClose={() => setAuditsDrawerOpen(false)}
        width={760}
      >
        <Paragraph type="secondary">
          Registro imutável de todas as criações, atualizações de código, novas versões, ativações e remoções de templates.
        </Paragraph>

        {auditsLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : audits.length === 0 ? (
          <Alert message="Nenhum registro de auditoria encontrado." type="info" showIcon />
        ) : (
          <Timeline
            mode="left"
            items={audits.map((item) => {
              const actionColors = {
                CREATE: 'green',
                UPDATE: 'blue',
                NEW_VERSION: 'purple',
                ACTIVATE: 'gold',
                DELETE: 'red',
              };

              const actionLabels = {
                CREATE: 'Criação de Modelo',
                UPDATE: 'Atualização de Código/Dados',
                NEW_VERSION: 'Nova Versão Criada',
                ACTIVATE: 'Versão Ativada',
                DELETE: 'Modelo Excluído',
              };

              return {
                color: actionColors[item.action] || 'gray',
                label: (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {new Date(item.createdAt).toLocaleString('pt-BR')}
                  </Text>
                ),
                children: (
                  <Card size="small" style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <Space>
                        <Tag color={actionColors[item.action] || 'default'} style={{ fontWeight: 600 }}>
                          {actionLabels[item.action] || item.action}
                        </Tag>
                        <Text strong>{item.name} (v{item.version || '1'})</Text>
                      </Space>
                      {(item.oldCode || item.newCode) && (
                        <Button
                          size="small"
                          type="link"
                          icon={<EyeOutlined />}
                          onClick={() => {
                            setSelectedAuditForDiff(item);
                            setDiffModalOpen(true);
                          }}
                        >
                          Ver Código / Diff
                        </Button>
                      )}
                    </div>

                    <Paragraph style={{ margin: '4px 0', fontSize: 13 }}>
                      {item.details || 'Ação executada no modelo de etiqueta'}
                    </Paragraph>

                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Responsável: <strong>{item.changedByName || 'Admin'}</strong>
                      {item.changedById && ` (ID: ${item.changedById.slice(0, 8)}...)`}
                    </Text>
                  </Card>
                ),
              };
            })}
          />
        )}
      </Drawer>

      {/* MODAL DE COMPARAÇÃO / VISUALIZADOR DE CÓDIGO DA AUDITORIA */}
      <Modal
        open={diffModalOpen}
        title={
          <Space>
            <HistoryOutlined style={{ color: '#1677ff' }} />
            <span>
              Registro de Auditoria: {selectedAuditForDiff?.name} (v{selectedAuditForDiff?.version})
            </span>
          </Space>
        }
        onCancel={() => {
          setDiffModalOpen(false);
          setSelectedAuditForDiff(null);
        }}
        footer={null}
        width={850}
      >
        {selectedAuditForDiff && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <Text>
                Data:{' '}
                <strong>
                  {new Date(selectedAuditForDiff.createdAt).toLocaleString('pt-BR')}
                </strong>
              </Text>
              <Text>
                Usuário: <strong>{selectedAuditForDiff.changedByName || 'Admin'}</strong>
              </Text>
            </div>

            <Alert message={selectedAuditForDiff.details} type="info" showIcon style={{ marginBottom: 16 }} />

            <div className="ltm-diff-container">
              <div>
                <Text strong style={{ color: '#cf1322' }}>
                  Código Anterior:
                </Text>
                <div className="ltm-diff-box old">
                  {selectedAuditForDiff.oldCode || '(Nenhum código anterior - Registro de criação)'}
                </div>
              </div>

              <div>
                <Text strong style={{ color: '#389e0d' }}>
                  Código Novo / Gravado:
                </Text>
                <div className="ltm-diff-box new">
                  {selectedAuditForDiff.newCode || '(Código removido - Registro de exclusão)'}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}